import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { ShieldCheck, Menu, X, LogIn, LogOut, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NAV } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import RotatingBanner from "@/components/shared/RotatingBanner";

const publicLinks = [
  { to: "/home", label: "Home", tid: NAV.home },
  { to: "/employers", label: "Employers", tid: NAV.companies },
  { to: "/security-officers", label: "Security Officers", tid: NAV.professionals },
  { to: "/elite-protection-network", label: "Elite", tid: "nav-elite" },
  { to: "/pricing", label: "Pricing", tid: NAV.pricing },
  { to: "/about", label: "About", tid: NAV.about },
  { to: "/contact", label: "Contact", tid: NAV.contact },
];

export default function PublicLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const dashPath = user?.role === "super_admin" ? "/admin" : user?.role === "company" ? "/company" : "/staff";

  return (
    <div className="min-h-screen flex flex-col bg-[#0A0A0A]">
      <RotatingBanner />
      {/* Top bar */}
      <header className="command-header sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-brand flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2.4} />
            </div>
            <div className="leading-none">
              <div className="font-outfit font-bold text-white text-lg tracking-tight">SecuSource</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-outfit">Security Workforce Platform</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {publicLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.tid}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-manrope font-medium rounded-md transition ${
                    isActive ? "text-white bg-white/10" : "text-slate-300 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            {user ? (
              <>
                <Button
                  variant="ghost"
                  data-testid={NAV.dashboard}
                  className="text-slate-200 hover:bg-white/10 hover:text-white"
                  onClick={() => nav(dashPath)}
                >
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                </Button>
                <Button
                  data-testid={NAV.logout}
                  variant="outline"
                  className="bg-transparent text-white border-white/20 hover:bg-white/10"
                  onClick={async () => { await logout(); nav("/"); }}
                >
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  data-testid={NAV.login}
                  className="text-slate-200 hover:bg-white/10 hover:text-white"
                  onClick={() => nav("/login")}
                >
                  <LogIn className="w-4 h-4 mr-2" /> Login
                </Button>
                <Button
                  data-testid={NAV.registerStaff}
                  className="bg-brand hover:bg-brand-hover text-white"
                  onClick={() => nav("/register/staff")}
                >
                  Join as Professional
                </Button>
              </>
            )}
          </div>

          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-white/10 bg-navy-900">
            <div className="px-4 py-3 flex flex-col gap-1">
              {publicLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`m-${l.tid}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-md text-sm font-medium ${isActive ? "bg-white/10 text-white" : "text-slate-300"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="border-t border-white/10 my-2" />
              {user ? (
                <>
                  <button className="nav-item" onClick={() => { setOpen(false); nav(dashPath); }} data-testid="m-nav-dashboard">
                    <LayoutDashboard className="w-4 h-4" /> Dashboard
                  </button>
                  <button className="nav-item" onClick={async () => { setOpen(false); await logout(); nav("/"); }} data-testid="m-nav-logout">
                    <LogOut className="w-4 h-4" /> Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="nav-item" data-testid="m-nav-login">
                    <LogIn className="w-4 h-4" /> Login
                  </Link>
                  <Link to="/register/staff" onClick={() => setOpen(false)} className="nav-item" data-testid="m-nav-register-staff">
                    Join as Professional
                  </Link>
                  <Link to="/register/company" onClick={() => setOpen(false)} className="nav-item" data-testid="m-nav-register-company">
                    Register as Company
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-white/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-md bg-brand flex items-center justify-center">
                <ShieldCheck className="w-4 h-4 text-black" />
              </div>
              <span className="font-outfit font-bold text-white text-lg">SecuSource</span>
            </div>
            <p className="text-sm text-white/60 max-w-sm font-manrope">
              Better teams. Faster deployment. A smarter way to source vetted security personnel and operational teams.
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[#FF7200] font-outfit font-bold mb-3">Platform</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/employers" className="hover:text-white">For Employers</Link></li>
              <li><Link to="/security-officers" className="hover:text-white">For Security Officers</Link></li>
              <li><Link to="/elite-protection-network" className="hover:text-white">Elite Protection Network</Link></li>
              <li><Link to="/cluster-teams" className="hover:text-white">Cluster Teams</Link></li>
              <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
              <li><Link to="/verify-badge" className="hover:text-white">Verify a licence</Link></li>
              <li><Link to="/trust" className="hover:text-white">Trust snapshot</Link></li>
              <li><Link to="/teams" className="hover:text-white">Browse teams</Link></li>
              <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
              <li><Link to="/reliability-policy" className="hover:text-white">Reliability policy</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[#FF7200] font-outfit font-bold mb-3">Company</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="hover:text-white">About</Link></li>
              <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
              <li><Link to="/login" className="hover:text-white">Login</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 text-xs text-white/50 flex justify-between">
            <span>© {new Date().getFullYear()} SecuSource. All rights reserved.</span>
            <span>Verified security professionals, ready when needed.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
